import { useCallback, useEffect, useState } from 'react';

import { requestPage } from '../../shared/api';
import { toQuery, type Filters } from '../../shared/filters';
import { EmptyState, ListSkeleton, renderFailure } from '../../shared/states';
import { useUpgrade } from '../../shared/upgrade';

/**
 * Результаты поиска (T11.6, `05_API.md` §5).
 *
 * ## Страницами, а не всё разом
 *
 * Выдача бывает в тысячи объявлений. Загрузить их одним запросом значит
 * заставить телефон разобрать мегабайт JSON до первой отрисовки — при
 * требовании `02_SPEC.md` §12.2 в две с половиной секунды на первый
 * экран это заведомый проигрыш. Берётся страница, дальше по кнопке.
 *
 * ## Кнопка, а не бесконечная прокрутка
 *
 * Бесконечная прокрутка отнимает у человека конец списка: он не знает,
 * просмотрел ли всё, и не может вернуться туда, где остановился. Здесь
 * это особенно неверно — человек ищет одну машину и хочет убедиться, что
 * не пропустил её.
 *
 * ## Уже виденное приглушается, а не прячется
 *
 * Сервер помечает такие объявления (`seen`). Спрятать их значило бы
 * менять выдачу под человеком: он вернулся, а список другой. Приглушение
 * сохраняет порядок и говорит «это ты смотрел».
 */

/** Объявление в том виде, в каком его отдаёт поиск. */
export interface Listing {
  readonly listing_id: string | number;
  readonly source_id: number;
  readonly brand_id: number | null;
  readonly model_id: number | null;
  readonly brand_name: string | null;
  readonly model_name: string | null;
  readonly year: number;
  readonly mileage_km: number | null;
  readonly price_minor: string | number;
  readonly region_id: number;
  readonly owners_count: number | null;
  readonly published_at: string;
  readonly market_delta_pct: number | null;
  readonly seen: boolean;
  readonly photo_url: string | null;
}

/** Сколько объявлений за раз. Хватает на два экрана прокрутки. */
const PAGE = 20;

/** Цена рублями без копеек: копейки в цене автомобиля не значат ничего. */
function price(minor: string | number): string {
  return `${Math.round(Number(minor) / 100).toLocaleString('ru-RU')} ₽`;
}

/**
 * Отклонение от рынка.
 *
 * Показывается только когда оно есть: «0 %» на несчитанной оценке
 * читалось бы как «ровно по рынку», то есть как утверждение вместо его
 * отсутствия.
 */
function delta(percent: number | null): { text: string; good: boolean } | null {
  if (percent === null || percent === 0) return null;

  return {
    text: `${percent > 0 ? '+' : '−'}${Math.abs(percent)} % к рынку`,
    good: percent < 0,
  };
}

function mileage(km: number | null): string | null {
  if (km === null) return null;

  return km < 1000 ? `${km} км` : `${Math.round(km / 1000)} тыс. км`;
}

/**
 * Одна строка выдачи.
 *
 * Порядок — порядок решений: сперва что за машина и почём, затем
 * подробности. Отклонение от рынка стоит рядом с ценой, потому что
 * вместе они и есть ответ на вопрос «стоит ли смотреть».
 */
function Row({
  listing,
  brands,
  regions,
  onOpen,
}: {
  listing: Listing;
  brands: Map<number, string>;
  regions: Map<number, string>;
  onOpen: (listingId: string | number) => void;
}): React.JSX.Element {
  // Название приходит от сервера; справочник остаётся запасным путём для
  // объявлений, записанных до того, как имена появились в ответе
  const brand =
    listing.brand_name ??
    (listing.brand_id === null ? null : (brands.get(listing.brand_id) ?? null));
  const name = [brand, listing.model_name].filter((one) => one !== null && one !== '').join(' ');
  const off = delta(listing.market_delta_pct);
  const facts = [
    mileage(listing.mileage_km),
    regions.get(listing.region_id) ?? null,
    listing.owners_count === null ? null : `владельцев: ${listing.owners_count}`,
  ].filter((one): one is string => one !== null);

  return (
    // Кнопкой, а не `div` с обработчиком: строка открывает карточку, и
    // это действие обязано доставаться клавиатуре и чтецу экрана так же,
    // как пальцу
    <button
      type="button"
      className={`card result${listing.seen ? ' result--seen' : ''}`}
      onClick={() => {
        onOpen(listing.listing_id);
      }}
    >
      {/*
        Без `width` и `height`. Атрибуты означают истинный размер снимка
        в точках, а не пропорции, и браузер отображает их в подсказки
        стиля: «4 на 3» дали высоту в три точки, и строка выдачи
        превратилась в полоску. Место под снимок держит `aspect-ratio`
        в стилях.

        Ленивая загрузка нужна: двадцать снимков разом съедают всю
        полосу мобильной сети.
      */}
      {listing.photo_url === null ? null : (
        <img className="result__photo" src={listing.photo_url} alt="" loading="lazy" />
      )}
      <span className="result__name">
        {name === '' ? 'Автомобиль' : name}, {listing.year}
      </span>
      {/*
        Внутри кнопки только строчные элементы: `div` и `p` там
        недопустимы по правилам разметки, и браузеры разбирают такую
        кнопку каждый по-своему. Раскладку задают стили
      */}
      <span className="result__price">
        {price(listing.price_minor)}
        {off === null ? null : (
          <span className={off.good ? 'result__delta result__delta--good' : 'result__delta'}>
            {off.text}
          </span>
        )}
      </span>
      {facts.length === 0 ? null : <span className="result__facts">{facts.join(' · ')}</span>}
    </button>
  );
}

export function ResultsScreen({
  filters,
  brands,
  regions,
  onOpen,
}: {
  filters: Filters;
  brands: Map<number, string>;
  regions: Map<number, string>;
  onOpen: (listingId: string | number) => void;
}): React.JSX.Element {
  const onUpgrade = useUpgrade();
  const [items, setItems] = useState<Listing[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'more'>('loading');
  const [error, setError] = useState<unknown>(null);

  const key = toQuery(filters);

  const load = useCallback(
    async (from: string | null) => {
      setError(null);
      setState(from === null ? 'loading' : 'more');

      try {
        const query = new URLSearchParams(key);
        query.set('limit', String(PAGE));

        if (from !== null) query.set('cursor', from);

        const page = await requestPage<Listing>(`/v1/listings?${query.toString()}`);

        // Первая страница заменяет список, следующая дополняет: иначе
        // «показать ещё» стирало бы то, что человек уже просмотрел
        setItems((current) => (from === null ? page.items : [...current, ...page.items]));
        setCursor(page.cursor);
        setHasMore(page.hasMore);
      } catch (failure: unknown) {
        setError(failure);
      } finally {
        setState('ready');
      }
    },
    [key],
  );

  // Смена фильтров — новый поиск с начала: продолжить прежним курсором
  // значит дополнить список объявлениями по старым условиям
  useEffect(() => {
    void load(null);
  }, [load]);

  if (state === 'loading') return <ListSkeleton rows={4} />;

  if (error !== null && items.length === 0) {
    const again = (): void => {
      void load(null);
    };

    return <>{renderFailure(error, { onRetry: again, onUpgrade })}</>;
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="Ничего не найдено"
        hint="Условия слишком узкие. Попробуйте поднять потолок цены или убрать часть фильтров."
      />
    );
  }

  return (
    <>
      <p className="screen__subtitle">
        Найдено: {items.length}
        {hasMore ? ' и ещё' : ''}
      </p>

      {items.map((listing) => (
        <Row
          key={`${listing.source_id}:${String(listing.listing_id)}`}
          listing={listing}
          brands={brands}
          regions={regions}
          onOpen={onOpen}
        />
      ))}

      {hasMore ? (
        <button
          type="button"
          className="button"
          disabled={state === 'more'}
          onClick={() => {
            void load(cursor);
          }}
        >
          {state === 'more' ? 'Загружаю…' : 'Показать ещё'}
        </button>
      ) : null}
    </>
  );
}
